import type { OverviewKpis } from "@/lib/types";
import { ByCountryModeToggle } from "./ByCountryModeToggle";
import { PredictionKpiTile } from "./PredictionKpiTile";

/** 2×2 grid of big-number KPI tiles shown in place of "Performance by
 *  country" when `byCountryMode === "prediction"`. Slots are hardcoded
 *  to the three D30 prediction metrics + one placeholder; making them
 *  user-configurable would bloat the toggle when the whole point of
 *  this panel is the predictions story.
 *
 *  Visuals reuse the `.kpi` class from the top KPI row so the tiles
 *  share styling (surface, border, big number, small label). They
 *  render slightly narrower since the right column of grid-2 is 1/3
 *  of page width vs 6-wide at the top — a 2×2 layout here yields
 *  tiles that are ≈1/6 of page width, matching the top row exactly. */
export function PredictionKpisCard({
  kpis,
  mode,
  onModeChange,
}: {
  kpis: OverviewKpis;
  mode: "country" | "prediction";
  onModeChange: (m: "country" | "prediction") => void;
}) {
  // Slot list — order defines grid placement (row-major, 2×2). The
  // 4th slot is intentionally a placeholder per the feature brief;
  // hidden behind `null` until we decide what goes there.
  const slots: ({ id: string } | null)[] = [
    { id: "predicted_revenue_d30" },
    { id: "predicted_roas_d30" },
    { id: "predicted_profit_d30" },
    null,
  ];

  return (
    <div className="card">
      <div className="card-head" style={{ alignItems: "center" }}>
        <h3>D30 predictions</h3>
        <div
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <ByCountryModeToggle value={mode} onChange={onModeChange} />
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 14,
          padding: "4px 16px 16px",
        }}
      >
        {slots.map((slot, i) =>
          slot ? (
            <PredictionKpiTile key={slot.id} metricId={slot.id} kpis={kpis} />
          ) : (
            // Placeholder tile — reserved real estate with a muted
            // hint so the grid keeps its shape. Same min-height as a
            // real tile so the card doesn't jump when a future
            // metric lands here.
            <div
              key={`placeholder-${i}`}
              className="kpi"
              style={{
                minHeight: 148,
                alignItems: "center",
                justifyContent: "center",
                color: "var(--c-text-4)",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              <div className="muted">Reserved for a future metric</div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
